import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import type { CustomFieldDataType } from './CustomFieldDefinitionModal';

/**
 * ApplyCustomFieldModal — take a field defined on one client and apply it to any
 * number of OTHER clients in one action (PRD 2.6c). Each target gets its own
 * scoped definition sharing the SAME deterministic field_key (label → key is
 * deterministic server-side), so the parcels custom-column / query view can
 * aggregate values across every client the field was applied to.
 *
 * Batching N POSTs client-side, partial-failure is explicit: a 409 (the client
 * already has the field) is treated as "already had it — skipped", NOT an error;
 * a genuine failure is surfaced without aborting the successful applications. A
 * compact result summary states how many were applied vs. skipped.
 *
 * No new backend and no schema change — it reuses POST
 * /api/custom-fields/definitions (per-scope UNIQUE index makes re-applying a
 * no-op) and GET /api/clients.
 */

interface ClientRow {
  id: string;
  name: string;
}

interface ApplyResult {
  applied: number;
  skipped: number;
  failed: Array<{ clientId: string; message: string }>;
}

export interface ApplyCustomFieldModalProps {
  open: boolean;
  onClose: () => void;
  /** The source field to fan out (its label/type/options define the new defs). */
  field: { label: string; data_type: CustomFieldDataType; options: string[] | null };
  /** The client this field currently lives on — excluded from the target list. */
  currentClientId: string;
  /** Fired after at least one successful apply so open target profiles refresh. */
  onApplied?: () => void;
}

export function ApplyCustomFieldModal({
  open, onClose, field, currentClientId, onApplied,
}: ApplyCustomFieldModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ApplyResult | null>(null);

  function reset() {
    setSelected(new Set()); setSearch(''); setBusy(false); setResult(null);
  }
  useEffect(() => { if (open) reset(); }, [open]);

  const clientsQuery = useQuery<ClientRow[]>({
    queryKey: ['clients-for-apply-field'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<ClientRow[] | { rows: ClientRow[] }>('/api/clients?limit=200', signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: open,
  });

  const targets = useMemo(() => {
    const all = (clientsQuery.data ?? []).filter((c) => c.id !== currentClientId);
    const q = search.trim().toLowerCase();
    return q ? all.filter((c) => c.name.toLowerCase().includes(q)) : all;
  }, [clientsQuery.data, currentClientId, search]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function apply() {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    const res: ApplyResult = { applied: 0, skipped: 0, failed: [] };
    // Sequential so one client's failure can never abort the rest and the
    // request volume stays bounded.
    for (const id of ids) {
      const body: Record<string, unknown> = {
        client_id: id, label: field.label, data_type: field.data_type,
      };
      if (field.data_type === 'select' && field.options) body.options = field.options;
      try {
        await api.post('/api/custom-fields/definitions', body);
        res.applied += 1;
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 409) {
          res.skipped += 1; // already had it — idempotent, not an error
        } else {
          res.failed.push({ clientId: id, message: err instanceof Error ? err.message : 'Failed' });
        }
      }
    }
    setBusy(false);
    setResult(res);
    // Keep the catalog + every scope's definition list in lockstep.
    queryClient.invalidateQueries({ queryKey: ['custom-field-catalog'] });
    queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    if (res.applied > 0) onApplied?.();
    if (res.failed.length > 0) {
      toast({ variant: 'error', message: `${res.failed.length} client${res.failed.length === 1 ? '' : 's'} could not be updated.` });
    }
  }

  const summary = result && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <p style={{ margin: 0, color: 'var(--color-ink)' }}>
        Applied to <strong>{result.applied}</strong> {result.applied === 1 ? 'client' : 'clients'}
        {result.skipped > 0 && (
          <> · <strong>{result.skipped}</strong> already had it</>
        )}
        {result.failed.length > 0 && (
          <> · <strong>{result.failed.length}</strong> failed</>
        )}.
      </p>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={`Apply "${field.label}" to other clients`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button variant="primary" onClick={apply} disabled={busy || selected.size === 0}>
              {busy ? 'Applying…' : `Apply${selected.size ? ` (${selected.size})` : ''}`}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {result ? (
          summary
        ) : (
          <>
            <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              Pick the clients that should also get this {field.data_type} field. Each gets its own
              copy sharing the same field key — a client that already has it is skipped.
            </p>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search businesses…"
            />
            {clientsQuery.isLoading ? (
              <p style={{ color: 'var(--color-ink-3)' }}>Loading businesses…</p>
            ) : targets.length === 0 ? (
              <p style={{ color: 'var(--color-muted)' }}>No other businesses to apply to.</p>
            ) : (
              <div style={{
                maxHeight: 260,
                overflowY: 'auto',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--color-card)',
                padding: 'var(--sp-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
              }}>
                {targets.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <label key={c.id} style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                      padding: 'var(--sp-1) var(--sp-2)', cursor: 'pointer',
                      fontSize: 'var(--fs-sm)', borderRadius: 'var(--r-sm)',
                      background: checked ? 'var(--brand-primary-bg)' : 'transparent',
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        style={{ accentColor: 'var(--brand-primary)' }}
                        onChange={() => toggle(c.id)}
                      />
                      <span>{c.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {selected.size > 0 && (
              <Badge variant="info">{selected.size} selected</Badge>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
